import React from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "src/components";
import { colors } from "src/theme";
import { formatNumber } from "src/common";

const Box = styled.div({
  flex: "1 1 0%",
  position: "relative",
  minWidth: 0,
  borderRadius: 8,
  padding: "4px",
  paddingBottom: "8px",
  backgroundColor: colors.background6,
});

const SavingsPill = styled.span({
  position: "absolute",
  display: "inline-block",
  top: 42,
  // left: 36,
  padding: "2px 8px",
  borderRadius: 6,
  backgroundColor: colors.note3,
});

const Struck = styled(Text)({
  textDecoration: "line-through",
});

interface OfferPriceSummaryProps {
  /** prices.generalTransferWithInstallments — "na rate" total. */
  installmentsTotal: number;
  /** prices.generalTransfer — "jednokratno" total. */
  oneTimeTotal: number;
  /** priceWithoutDiscounts — struck-through list price. */
  priceWithoutDiscounts?: number;
}

const pct = (from: number, to: number) =>
  from > 0 ? Math.round(((from - to) / from) * 100) : 0;

const SavingsTag = ({ text }: { text: string }) => (
  <SavingsPill>
    <Text type="tiny" color={colors.text3}>{text}</Text>
  </SavingsPill>
);

const PriceLine = ({ struck, total, tag }: { struck?: number; total: number, tag?: string; }) => (
  <Flex align="center" justify="flex-start" gap={6} wrap="wrap">
    {typeof struck === "number" && struck > total && (
      <Struck type="small" color={colors.text4}>
        {formatNumber(struck, "", 0)}
      </Struck>
    )}
    <Flex vertical align="flex-start">
      <Flex gap={2} align="center">
        <Text type="base" bold color={colors.text1}>{`${formatNumber(total, "", 0)}€`}</Text>
        <Text type="small" bold color={colors.text1}>godišnje</Text>
      </Flex>
      {tag && <SavingsTag text={tag} />}
    </Flex>
  </Flex>
);

/**
 * Offer total(s) block (Figma redesign).
 * Two boxes ("na rate" vs "jednokratno") when the totals differ,
 * a single centered "ukupno" box when they are equal.
 * Savings badge splits the base list discount from the extra
 * one-time-payment bonus, e.g. "15%+20% uštede".
 */
export const OfferPriceSummary = ({ installmentsTotal, oneTimeTotal, priceWithoutDiscounts }: OfferPriceSummaryProps) => {
  const equal = Math.round(installmentsTotal) === Math.round(oneTimeTotal);
  const basePct = priceWithoutDiscounts ? pct(priceWithoutDiscounts, installmentsTotal) : 0;
  const oneTimeBonusPct = pct(installmentsTotal, oneTimeTotal);

  const baseTag = basePct > 0 ? `${basePct}% uštede` : undefined;

  const buildOneTimeTag = (): string | undefined => {
    if (basePct > 0 && oneTimeBonusPct > 0) return `${basePct}%+${oneTimeBonusPct}% uštede`;
    if (basePct > 0) return `${basePct}% uštede`;
    if (oneTimeBonusPct > 0) return `${oneTimeBonusPct}% uštede`;
    return undefined;
  };
  const oneTimeTag = buildOneTimeTag();

  if (equal) {
    return (
      <Box>
        <Flex vertical gap={4} align="center">
          <Text type="tiny" color={colors.text3}>ukupno</Text>
          <PriceLine struck={priceWithoutDiscounts} total={installmentsTotal} tag={baseTag} />
        </Flex>
      </Box>
    );
  }

  return (
    <Flex gap={8} align="stretch" style={{ paddingBottom: 8 }}>
      <Box>
        <Flex vertical align="center">
          <Text type="micro" color={colors.text3}>ukupno za plaćanje na rate</Text>
          <PriceLine struck={priceWithoutDiscounts} total={installmentsTotal} tag={baseTag} />
        </Flex>
      </Box>
      <Box>
        <Flex vertical align="center">
          <Text type="micro" color={colors.text3}>ukupno za jednokratno plaćanje</Text>
          <PriceLine struck={priceWithoutDiscounts} total={oneTimeTotal} tag={oneTimeTag} />
        </Flex>
      </Box>
    </Flex>
  );
};
